import React, {
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useReducer,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';

import useSessionStore from '../../hooks/useSessionStore';
import UserContext from '../UserContext';
import InternalAssignment from '../../Model/InternalAssignment';
import CoachPod from '../../Model/CoachPod';
import User from '../../Model/User';
import Coach from '../../Model/Coach';

import PodContext from './PodContext';

const initialPodData = {
  selectedPodId: null,
  pods: [],
  users: null,
  isReady: false,
  assistantCoaches: [],
  coachInternalAssignments: [],
};

function sortPods(pods) {
  if (!Array.isArray(pods) || pods.length === 0) return pods;
  return [...pods].sort((a, b) => a.name.localeCompare(b.name, undefined, {
    numeric: true,
    sensitivity: 'base',
  }));
}

function podDataReducer(state, action) {
  switch (action.type) {
    case 'SET_INITIAL_DATA':
      return {
        ...action.payload,
        pods: sortPods(action.payload.pods),
      };
    case 'SET_POD_LOADING':
      return { ...state, isReady: false };
    case 'SET_USERS_AND_SELECTED_POD_ID':
      return {
        ...state, users: action.users, selectedPodId: action.selectedPodId, isReady: true,
      };
    case 'SET_PODS':
      return {
        ...state,
        pods: sortPods(action.pods),
      };
    case 'SET_ASSISTANT_COACHES_AND_ASSIGNMENTS':
      return {
        ...state,
        assistantCoaches: action.assistantCoaches,
        coachInternalAssignments: action.coachInternalAssignments,
      };
    default:
      return state;
  }
}

/**
 * Extracts only the necessary data from a CoachPod document object
 * to optimize memory usage and performance in the PodContext.
 *
 * @param {Object} pod - The CoachPod document object from Firestore
 * @param {string} pod.id - The document ID
 * @param {Object} pod.data - The document data
 * @param {string} pod.data.name - The pod name
 * @param {number} pod.data.userCount - The number of users in the pod
 * @param {string} pod.data.coach - The coach ID associated with the pod
 * @param {number} pod.data.maxUsers - The maximum number of users allowed in the pod
 * @returns {{id: string, name: string, userCount: number, coach: string, maxUsers: number}}
 *   The extracted pod data with only necessary properties
 */
function getPodData(pod) {
  return {
    id: pod.id,
    name: pod.data.name,
    userCount: pod.data.userCount,
    coach: pod.data.coach,
    maxUsers: pod.data.maxUsers,
  };
}

const PodContextProvider = ({ children }) => {
  const { authUser: { uid: authUserId } = {} } = useSessionStore();
  const { userId: userInRouteId } = useContext(UserContext);

  const [podData, dispatch] = useReducer(podDataReducer, initialPodData);

  const init = useCallback(async () => {
    dispatch({ type: 'SET_POD_LOADING' });
    let selectedPodId = null;
    let pods = [];
    let users = null;

    // Parallel fetch of initial data
    const [assignments, podSnap] = await Promise.all([
      InternalAssignment.getAllCurrentAssignments(authUserId, 'AC', userInRouteId, {
        mode: 'auto',
        minimizeUpdates: true,
      }),
      CoachPod.getPodsByCoach(userInRouteId),
    ]);

    if (assignments.hasDocs) {
      selectedPodId = assignments.docs[0].data.podId;
      if (selectedPodId) {
        const usersRef = await User.getUserByPodId(selectedPodId, userInRouteId);
        if (usersRef.hasDocs) {
          users = usersRef.docs.map((doc) => doc.id);
        }
      }
    }

    if (podSnap.hasDocs) {
      pods = podSnap.docs.map(getPodData);
    }

    dispatch({
      type: 'SET_INITIAL_DATA',
      payload: {
        selectedPodId,
        pods,
        users,
        isReady: true,
        assistantCoaches: [],
        coachInternalAssignments: [],
      },
    });
  }, [authUserId, userInRouteId]);

  useEffect(() => {
    init();
  }, [init]);

  const updateSelectedPodId = useCallback(async (newPodId) => {
    dispatch({ type: 'SET_POD_LOADING' });
    let users = null;
    if (newPodId) {
      const usersRef = await User.getUserByPodId(newPodId, userInRouteId);
      if (usersRef.hasDocs) {
        users = usersRef.docs.map((doc) => doc.id);
      }
    }
    dispatch({ type: 'SET_USERS_AND_SELECTED_POD_ID', users, selectedPodId: newPodId });
  }, [userInRouteId]);

  const fetchPodsForCoach = useCallback(async (coachId) => {
    let pods = [];
    if (coachId) {
      const podsRef = await CoachPod.getPodsByCoach(coachId);
      if (podsRef.hasDocs) {
        pods = podsRef.docs.map(getPodData);
      }
    }
    dispatch({ type: 'SET_PODS', pods });
  }, []);

  const setAssistantCoachesAndAssignment = useCallback(async (coachId) => {
    let assistantCoaches = [];
    let coachInternalAssignments = [];

    if (coachId) {
      const [assistantCoachSnap, internalAssignmentSnap] = await Promise.all([
        Coach.getAssistantCoaches(),
        InternalAssignment.getAllCurrentAssignments(null, 'AC', coachId),
      ]);

      if (internalAssignmentSnap.hasDocs) {
        coachInternalAssignments = internalAssignmentSnap.docs.map((assignment) => ({
          id: assignment.id,
          podId: assignment.data.podId,
          userId: assignment.data.userId,
          userName: assignment.data.userName,
        }));
      }

      if (internalAssignmentSnap.hasDocs && assistantCoachSnap.hasDocs) {
        const filteredAssistantCoaches = assistantCoachSnap.docs.filter(
          (doc) => internalAssignmentSnap.docs.some((assignment) => assignment.data.userId === doc.id),
        );
        assistantCoaches = filteredAssistantCoaches.map((coach) => ({
          id: coach.id,
          firstName: coach.data.firstName,
        }));
      }

      dispatch({ type: 'SET_ASSISTANT_COACHES_AND_ASSIGNMENTS', coachInternalAssignments, assistantCoaches });
    }
  }, []);

  const context = useMemo(() => ({
    selectedPodId: podData.selectedPodId,
    pods: podData.pods,
    users: podData.users,
    isReady: podData.isReady,
    assistantCoaches: podData.assistantCoaches,
    coachInternalAssignments: podData.coachInternalAssignments,
    updateSelectedPodId,
    fetchPodsForCoach,
    setAssistantCoachesAndAssignment,
  }), [
    podData.selectedPodId,
    podData.pods,
    podData.users,
    podData.isReady,
    podData.assistantCoaches,
    podData.coachInternalAssignments,
    updateSelectedPodId,
    fetchPodsForCoach,
    setAssistantCoachesAndAssignment,
  ]);

  return (
    <PodContext.Provider value={context}>
      {children}
    </PodContext.Provider>
  );
};

PodContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(PodContextProvider);
